import React from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { SnackbarProvider } from 'notistack'
import { StockScreen } from './pages/StockScreen'
import { StockScreenEco } from './pages/StockScreenEco'
import { DashboardScreen } from './pages/DashboardScreen'
import { LoginScreen } from './pages/LoginScreen'
import { ForgotPasswordScreen } from './pages/ForgotPasswordScreen'
import { ResetPasswordScreen } from './pages/ResetPasswordScreen'
import { CreateUserScreen } from './pages/CreateUserScreen'
import { AdminPanelScreen } from './pages/AdminPanelScreen'
import { EditUserScreen } from './pages/EditUserScreen'
import { ProfileScreen } from './pages/ProfileScreen'
import { PrivateRoute } from './component/shared/PrivateRoute'
import {
  HTL_STOCK,
  ECO_STOCK,
  ADMIN_PANEL,
  SALES_STATS,
  TLP_POINT,
  TRIP_POINT,
  NEXEN_POINT,
  STAFF_BENEFIT_ADMIN,
  TYREHUB_POINT,
  BATTERY_INCENTIVE_POINT,
  MOBIL_NORTH_CHINA_TRIP_POINT,
  FIESTA_POINT,
  SALES_COMMISSION,
  SALES_PAYOUT,
} from '../src/constant/permission'
import { UserAuthProvider } from './api/context/UserAuthContext'
import { Helmet } from 'react-helmet-async'
import { SalesStatsScreen } from './pages/SalesStatsScreen'
import { TlpPointPanelScreen } from './pages/TlpPointPanelScreen'
import { TlpAccountSummaryScreen } from './pages/TlpAccountSummaryScreen'
import { TripPointPanelScreen } from './pages/TripPointPanelScreen'
import { TripAccountSummaryScreen } from './pages/TripAccountSummaryScreen'
import { NexenPointPanelScreen } from './pages/NexenPointPanelScreen'
import { NexenAccountSummaryScreen } from './pages/NexenPointAccountSummaryScreen'
import { StaffBenefitPanelScreen } from './pages/StaffBenefitPanelScreen'
import { CreateStaffBenefit } from './pages/CreateStaffBenefitScreen'
import { StaffBenefitUserPanelScreen } from './pages/StaffBenefitUserPanelScreen'
import { StaffBenefitClaimPanelScreen } from './pages/StaffBenefitClaimPanelScreen'
import { StaffBenefitReviewPanelScreen } from './pages/StaffBenefitReviewPanelScreen'
import { NexenTripAccountSummaryScreen } from './pages/NexenTripPointAccountSummaryScreen'
import { NexenTripPointPanelScreen } from './pages/NexenTripPointPanelScreen'
import { TyrehubPoints } from './pages/tyrehub-point/TyrehubPoints'
import { TyrehubPoint } from './pages/tyrehub-point/TyrehubPoint'
import { BatteryIncentivePoints } from './pages/battery-incentive-point/BatteryIncentivePoints'
import { BatteryIncentivePoint } from './pages/battery-incentive-point/BatteryIncentivePoint'
import { MobilNorthChinaTripPoint } from './pages/trip-north-point/MobilNorthChinaTripPoint'
import { MobilNorthChinaTripPoints } from './pages/trip-north-point/MobilNorthChinaTripPoints'
import { CompanyMappingScreen } from './pages/company-mapping/Page'
import { FiestaPoints } from './pages/fiesta-point/FiestaPoints'
import { Commission } from './pages/sales-commission/Page'
import { Payout } from './pages/sales-payout/Page'

const queryClient = new QueryClient()

const theme = createTheme({
  palette: {
    primary: {
      main: '#000',
    },
    secondary: {
      main: '#fff',
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    holiday: {
      main: '#4caf50',
    },
  },
  typography: {
    h1: {
      fontSize: '26px',
      fontWeight: '500',
    },
    h2: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#222222',
    },
    h3: {
      fontSize: '25px',
      fontWeight: '200',
      color: '#565656',
    },
    h4: {
      fontSize: '18px',
      fontWeight: '500',
      color: '#323232',
    },
    h5: {
      fontSize: '18px',
      fontWeight: '500',
    },
    subtitle1: {
      fontSize: '22px',
      fontWeight: '300',
    },
    subtitle2: {
      fontSize: '8px',
      fontWeight: '500',
    },
    button: {
      fontWeight: '500',
    },
  },
})

const App = () => {
  const routes = [
    { path: '/', component: DashboardScreen },
    { path: '/htl-stocklist', component: StockScreen, permission: HTL_STOCK },
    { path: '/eco-stocklist', component: StockScreenEco, permission: ECO_STOCK },
    { path: '/create-user', component: CreateUserScreen, permission: ADMIN_PANEL },
    { path: '/edit-user', component: EditUserScreen, permission: ADMIN_PANEL },
    { path: '/admin', component: AdminPanelScreen, permission: ADMIN_PANEL },
    { path: '/staff-benefit', component: StaffBenefitPanelScreen, permission: STAFF_BENEFIT_ADMIN },
    {
      path: '/create-staff-benefit',
      component: CreateStaffBenefit,
      permission: STAFF_BENEFIT_ADMIN,
    },
    { path: '/user-staff-benefit', component: StaffBenefitUserPanelScreen },
    { path: '/staff-benefit-claims', component: StaffBenefitClaimPanelScreen },
    { path: '/claims-review', component: StaffBenefitReviewPanelScreen },
    { path: '/company-mapping', component: CompanyMappingScreen },
    { path: '/my-profile', component: ProfileScreen },
    { path: '/revenue-dashboard', component: SalesStatsScreen, permission: SALES_STATS },
    { path: '/tlp-point', component: TlpPointPanelScreen, permission: TLP_POINT },
    { path: '/tlp-summary', component: TlpAccountSummaryScreen, permission: TLP_POINT },
    { path: '/trip-point', component: TripPointPanelScreen, permission: TRIP_POINT },
    { path: '/trip-summary', component: TripAccountSummaryScreen, permission: TRIP_POINT },
    { path: '/nexen-point', component: NexenPointPanelScreen, permission: NEXEN_POINT },
    { path: '/nexen-summary', component: NexenAccountSummaryScreen, permission: NEXEN_POINT },
    { path: '/nexen-trip-point', component: NexenTripPointPanelScreen, permission: NEXEN_POINT },
    {
      path: '/nexen-trip-summary',
      component: NexenTripAccountSummaryScreen,
      permission: NEXEN_POINT,
    },
    { path: '/tyrehub-trip-point', component: TyrehubPoints, permission: TYREHUB_POINT },
    { path: '/tyrehub-trip-summary', component: TyrehubPoint, permission: TYREHUB_POINT },
    {
      path: '/battery-incentive-point',
      component: BatteryIncentivePoints,
      permission: BATTERY_INCENTIVE_POINT,
    },
    {
      path: '/battery-incentive-summary',
      component: BatteryIncentivePoint,
      permission: BATTERY_INCENTIVE_POINT,
    },
    {
      path: '/mobil-north-china-trip-point',
      component: MobilNorthChinaTripPoints,
      permission: MOBIL_NORTH_CHINA_TRIP_POINT,
    },
    {
      path: '/mobil-north-china-trip-summary',
      component: MobilNorthChinaTripPoint,
      permission: MOBIL_NORTH_CHINA_TRIP_POINT,
    },
    { path: '/fiesta-point', component: FiestaPoints, permission: FIESTA_POINT },
    {
      path: '/sales-commission',
      component: Commission,
      permission: SALES_COMMISSION,
    },
    {
      path: '/sales-payout',
      component: Payout,
      permission: SALES_PAYOUT,
    },
  ]

  return (
    <HelmetProvider>
      <Helmet defaultTitle='Staff Portal'>
        <title>Staff Portal</title>
      </Helmet>
      <SnackbarProvider>
        <Router>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
              <UserAuthProvider>
                <Routes>
                  {routes.map(({ path, component: Component, permission }) => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <PrivateRoute permission={permission}>
                          <Component />
                        </PrivateRoute>
                      }
                    />
                  ))}
                  <Route path='/login' element={<LoginScreen />} />
                  <Route path='/forgot-password' element={<ForgotPasswordScreen />} />
                  <Route path='/reset-password' element={<ResetPasswordScreen />} />
                </Routes>
              </UserAuthProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </Router>
      </SnackbarProvider>
    </HelmetProvider>
  )
}

export default App
